import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

// ─── Antifraud types ──────────────────────────────────────────────────────────

export type AntifraudPreset = 'PERMISSIVE' | 'BALANCED' | 'STRICT' | 'CUSTOM';

export interface AntifraudReadingConfig {
  /** Punteggio ≥ soglia → auto-approvazione (0..1). */
  autoApprove: number;
  /** Punteggio ≤ soglia → auto-rifiuto (0..1). */
  autoReject: number;
  /** Punteggio ≥ soglia → zona verde (alta confidenza) (0..1). */
  green: number;
  /** Punteggio ≥ soglia → zona gialla (confidenza media) (0..1). */
  yellow: number;
}

export interface AntifraudAuthenticityConfig {
  /** Score ≥ soglia → zona verde (0..100). */
  green: number;
  /** Score ≥ soglia → zona gialla (0..100). */
  yellow: number;
  /** Pavimento minimo per segnali soft (0..100). */
  softFloor: number;
}

export interface AntifraudConfig {
  preset: AntifraudPreset;
  reading: AntifraudReadingConfig;
  authenticity: AntifraudAuthenticityConfig;
  /** Soglia disparità R alto / V basso → segnalazione (0..100). */
  disparityThreshold: number;
  /** Soglia distanza Hamming pHash per duplicati (0..64). */
  phashHammingThreshold: number;
}

export type PartnerLayoutTier = 'MAIN' | 'GOLD' | 'SILVER' | 'TECHNICAL';

export interface PartnerTierLayoutItem {
  tier: PartnerLayoutTier;
  columns: 2 | 3;
  /** Presente nelle response; il BO non lo invia, e' derivato dal backend. */
  sourceTiers?: string[];
}

// ─── Shop taxonomy types (T-69) ──────────────────────────────────────────────

export interface ShopCustomizationPricing {
  /** Sovrapprezzo nome (€). */
  name: number;
  /** Sovrapprezzo numero per numero di cifre (€). */
  numberByDigits: { '1': number; '2': number };
  /** Numero massimo consentito. */
  numberMax: number;
}

export interface ShopTaxonomy {
  macrocategories: string[];
  subcategoriesByMacro: Record<string, string[]>;
  sizeSets: { senior: string[]; junior: string[] };
  customizationPricing: ShopCustomizationPricing;
  /**
   * Nomi delle macro-categorie DISATTIVATE. Disattivare = aggiungere qui il nome;
   * nulla viene eliminato e la macro resta comunque in `macrocategories`.
   */
  inactiveMacrocategories?: string[];
}

// ─────────────────────────────────────────────────────────────────────────────

export interface AdminTenantConfigDto {
  id: string;
  tenantId: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string | null;
  logoUrl: string | null;
  clubName: string;
  clubCity: string | null;
  clubStadium: string | null;
  clubFounded: number | null;
  appName: string | null;
  stadiumLat: number | null;
  stadiumLon: number | null;
  checkinRadiusM: number;
  checkinQrMode: string;
  checkinWindowMinutesBefore: number;
  checkinWindowMinutesAfter: number;
  checkinRejectMockedGps: boolean;
  checkinPointsWithGeo: number;
  checkinPointsWithoutGeo: number;
  hideNextMatch: boolean;
  partnersVisible: boolean;
  partnerTierLayout: PartnerTierLayoutItem[];
  shopVisible: boolean;
  shopPlaceholderMessage: string | null;
  shippingEnabled: boolean;
  /** T-251: notifica anche sul gol dell'avversario (impostazione del club). */
  pushOnOpponentGoal: boolean;
  subscriberDiscountPct: number;
  subscriptionEndsAt: string | null;
  loyaltyRegulationUrl: string | null;
  // T-116: le 4 date fasi non sono più in config — sono righe LoyaltyPeriod
  // (gestite nella pagina "Periodi di raccolta").
  minAppVersion: string;
  defaultLocale: string;
  timezone: string;
  configVersion: number;
  distribution: string;
  metadata: unknown;
  updatedAt: string;
  /** Configurazione antifrode. Null/assente su tenant non ancora configurati. */
  antifraudConfig?: AntifraudConfig | null;
  /** Tassonomia shop (macro/sotto-categorie, set taglie, prezzi personalizzazione). Null se non configurata. */
  shopTaxonomy?: ShopTaxonomy | null;
}

export type UpdateConfigInput = Partial<
  Pick<
    AdminTenantConfigDto,
    | 'primaryColor'
    | 'secondaryColor'
    | 'accentColor'
    | 'logoUrl'
    | 'clubName'
    | 'clubCity'
    | 'clubStadium'
    | 'clubFounded'
    | 'appName'
    | 'stadiumLat'
    | 'stadiumLon'
    | 'checkinRadiusM'
    | 'checkinQrMode'
    | 'checkinWindowMinutesBefore'
    | 'checkinWindowMinutesAfter'
    | 'checkinRejectMockedGps'
    | 'checkinPointsWithGeo'
    | 'checkinPointsWithoutGeo'
    | 'hideNextMatch'
    | 'partnersVisible'
    | 'partnerTierLayout'
    | 'shopVisible'
    | 'shopPlaceholderMessage'
    | 'shippingEnabled'
    | 'pushOnOpponentGoal'
    | 'subscriberDiscountPct'
    | 'subscriptionEndsAt'
    | 'loyaltyRegulationUrl'
    | 'minAppVersion'
    | 'defaultLocale'
    | 'timezone'
  > & {
    /** JSON free-form (storeUrl, forceUpdateBelow, …). */
    metadata: Record<string, unknown>;
    /** Configurazione antifrode. Inviare null per resettare al default Bilanciato. */
    antifraudConfig?: AntifraudConfig | null;
    /** Tassonomia shop. */
    shopTaxonomy?: ShopTaxonomy | null;
  }
>;

const QK = ['tenant-admin-config'] as const;

export function useTenantAdminConfig() {
  return useQuery({
    queryKey: QK,
    queryFn: async () => {
      const res = await apiClient.get<AdminTenantConfigDto>('/tenant/admin-config');
      return res.data;
    },
  });
}

export function useUpdateTenantConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateConfigInput) => {
      const res = await apiClient.patch<AdminTenantConfigDto>('/tenant/admin-config', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}
